/*
 * Super Admin → Content → Experts.
 * Manage the public Experts roster: bios, order, and a focal-point + zoom photo
 * editor. Images are stored in managed object storage (not hardcoded paths) and
 * the public Experts page + landing render them with the saved focal-point/zoom.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { toast } from "@/components/ui/sonner";
import { Loader2, Upload, Trash2, Plus, ArrowUp, ArrowDown, Save, Users, ImageOff } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const PRODUCT_ROUTE = "/myschoolrx";
const clamp = (n, lo, hi) => Math.max(lo, Math.min(hi, n));

/* Interactive circular avatar: drag to reposition, zoom via slider. */
const AvatarEditor = ({ exp, onChange, headers }) => {
  const boxRef = useRef(null);
  const drag = useRef(null);
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const fx = Number.isFinite(exp.img_focal_x) ? exp.img_focal_x : 50;
  const fy = Number.isFinite(exp.img_focal_y) ? exp.img_focal_y : 50;
  const zoom = Number(exp.img_zoom) > 0 ? Number(exp.img_zoom) : 1;

  const onDown = (e) => {
    if (!exp.photo) return;
    const p = e.touches ? e.touches[0] : e;
    drag.current = { x: p.clientX, y: p.clientY, fx, fy, w: boxRef.current.offsetWidth, h: boxRef.current.offsetHeight };
  };
  const onMove = (e) => {
    if (!drag.current) return;
    const p = e.touches ? e.touches[0] : e;
    const dx = ((p.clientX - drag.current.x) / drag.current.w) * 100;
    const dy = ((p.clientY - drag.current.y) / drag.current.h) * 100;
    onChange({ img_focal_x: clamp(Math.round(drag.current.fx - dx), 0, 100), img_focal_y: clamp(Math.round(drag.current.fy - dy), 0, 100) });
  };
  const onUp = () => { drag.current = null; };

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await axios.post(`${API}/api/assets/expert-image`, fd, { headers });
      onChange({ photo: r.data.url, img_focal_x: 50, img_focal_y: 50, img_zoom: 1 });
      toast.success("Photo uploaded");
    } catch { toast.error("Upload failed (max 5 MB, JPG/PNG/WEBP)"); } finally { setUploading(false); }
  };

  return (
    <div className="flex flex-col items-center gap-3">
      <div
        ref={boxRef}
        className="relative h-32 w-32 rounded-full overflow-hidden border-2 border-[#e5e7eb] bg-[#f3f4f6] cursor-grab active:cursor-grabbing select-none"
        onMouseDown={onDown} onMouseMove={onMove} onMouseUp={onUp} onMouseLeave={onUp}
        onTouchStart={onDown} onTouchMove={onMove} onTouchEnd={onUp}
        data-testid={`expert-avatar-editor-${exp.id}`}
      >
        {exp.photo ? (
          <img src={exp.photo} alt={exp.name} draggable={false}
            className="h-full w-full object-cover pointer-events-none"
            style={{ objectPosition: `${fx}% ${fy}%`, transform: `scale(${zoom})` }} />
        ) : (
          <div className="h-full w-full flex items-center justify-center text-white text-3xl font-bold" style={{ background: "#0b1220" }}>
            {exp.initials || "?"}
          </div>
        )}
      </div>
      {exp.photo && <div className="text-[11.5px] text-[#9ca3af]">Drag the photo to reposition</div>}
      <div className="w-full max-w-[200px]">
        <div className="flex items-center justify-between text-[11.5px] text-[#6b7280] mb-1"><span>Zoom</span><span>{zoom.toFixed(2)}×</span></div>
        <Slider min={1} max={2.5} step={0.05} value={[zoom]} disabled={!exp.photo}
          onValueChange={(v) => onChange({ img_zoom: v[0] })} data-testid={`expert-zoom-${exp.id}`} />
      </div>
      <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden"
        onChange={(e) => upload(e.target.files?.[0])} data-testid={`expert-file-${exp.id}`} />
      <div className="flex gap-2">
        <Button size="sm" variant="outline" disabled={uploading} onClick={() => fileRef.current?.click()} data-testid={`expert-upload-${exp.id}`}>
          {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
          <span className="ml-1.5">{exp.photo ? "Replace" : "Upload"}</span>
        </Button>
        {exp.photo && (
          <Button size="sm" variant="ghost" className="text-red-600" onClick={() => onChange({ photo: "" })} data-testid={`expert-remove-photo-${exp.id}`}>
            <ImageOff className="h-3.5 w-3.5 mr-1" />Remove
          </Button>
        )}
      </div>
    </div>
  );
};

export default function ExpertsAdminPage({ session }) {
  const [product, setProduct] = useState(null);
  const [experts, setExperts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const headers = {
    "x-user-role": session?.user?.role || "admin",
    "x-user-id": session?.user?.id || "",
  };

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/products`);
      const p = (r.data || []).find((x) => x.preview_route === PRODUCT_ROUTE) || (r.data || [])[0];
      setProduct(p);
      setExperts((p?.section_content?.experts?.list || []).map((e) => ({ ...e })));
    } catch { toast.error("Failed to load experts"); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const update = (i, patch) => setExperts((prev) => prev.map((e, idx) => (idx === i ? { ...e, ...patch } : e)));
  const move = (i, dir) => setExperts((prev) => {
    const next = [...prev]; const j = i + dir;
    if (j < 0 || j >= next.length) return prev;
    [next[i], next[j]] = [next[j], next[i]]; return next;
  });
  const addExpert = () => setExperts((prev) => [...prev, {
    id: `expert_${Date.now().toString(36)}`, name: "New Expert", title: "", bio: "", initials: "NE",
    expertise: [], photo: "", img_focal_x: 50, img_focal_y: 50, img_zoom: 1,
  }]);
  const removeExpert = (i) => setExperts((prev) => prev.filter((_, idx) => idx !== i));

  const save = async () => {
    if (!product) return;
    setSaving(true);
    try {
      const sc = { ...(product.section_content || {}) };
      // Persist array order into the `order` field the public page sorts by,
      // and default visibility to active unless explicitly turned off.
      const listWithOrder = experts.map((e, idx) => ({ ...e, order: idx + 1, active: e.active !== false }));
      sc.experts = { ...(sc.experts || {}), list: listWithOrder };
      await axios.patch(`${API}/api/admin/products/${product.id}`, { section_content: sc }, { headers });
      setExperts(listWithOrder);
      setProduct((p) => ({ ...p, section_content: sc }));
      toast.success("Experts saved");
    } catch { toast.error("Save failed"); } finally { setSaving(false); }
  };

  if (loading) return <div className="py-16 text-center text-[#6b7280]"><Loader2 className="h-5 w-5 animate-spin inline" /></div>;

  return (
    <div className="mx-auto max-w-4xl px-4 sm:px-6 py-8" data-testid="experts-admin-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 text-[#0b1220]"><Users className="h-6 w-6 text-[#007a9e]" /><h1 className="text-2xl font-bold">Experts</h1></div>
          <p className="mt-1 text-sm text-[#6b7280]">Manage the public Experts roster, photos, and display framing.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={addExpert} data-testid="experts-add"><Plus className="h-4 w-4 mr-1.5" />Add expert</Button>
          <Button onClick={save} disabled={saving} className="bg-[#0b1220] hover:bg-black text-white" data-testid="experts-save">
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}Save
          </Button>
        </div>
      </div>

      <div className="mt-6 space-y-4">
        {experts.map((exp, i) => (
          <div key={exp.id || i} className={`rounded-xl border bg-white p-5 ${exp.active === false ? "border-[#e5e7eb] opacity-70" : "border-[#e5e7eb]"}`} data-testid={`expert-admin-card-${exp.id}`}>
            <div className="flex items-center justify-end gap-2 mb-2">
              <span className="text-[11.5px] text-[#6b7280]">{exp.active === false ? "Hidden on site" : "Visible on site"}</span>
              <Switch checked={exp.active !== false} onCheckedChange={(v) => update(i, { active: v })} data-testid={`expert-active-${exp.id}`} />
            </div>
            <div className="flex flex-col md:flex-row gap-6">
              <AvatarEditor exp={exp} onChange={(patch) => update(i, patch)} headers={headers} />
              <div className="flex-1 space-y-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div><label className="text-[11.5px] text-[#6b7280]">Name</label><Input value={exp.name || ""} onChange={(e) => update(i, { name: e.target.value })} data-testid={`expert-name-${exp.id}`} /></div>
                    <div><label className="text-[11.5px] text-[#6b7280]">Initials (fallback)</label><Input value={exp.initials || ""} maxLength={3} onChange={(e) => update(i, { initials: e.target.value.toUpperCase() })} data-testid={`expert-initials-input-${exp.id}`} /></div>
                  </div>
                  <div className="flex flex-col gap-1 pt-4">
                    <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => move(i, -1)} data-testid={`expert-up-${exp.id}`}><ArrowUp className="h-3.5 w-3.5" /></Button>
                    <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => move(i, 1)} data-testid={`expert-down-${exp.id}`}><ArrowDown className="h-3.5 w-3.5" /></Button>
                    <Button size="icon" variant="ghost" className="h-7 w-7 text-red-600" onClick={() => removeExpert(i)} data-testid={`expert-delete-${exp.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                  </div>
                </div>
                <div><label className="text-[11.5px] text-[#6b7280]">Title</label><Input value={exp.title || ""} onChange={(e) => update(i, { title: e.target.value })} data-testid={`expert-title-${exp.id}`} /></div>
                <div><label className="text-[11.5px] text-[#6b7280]">Bio</label><Textarea rows={3} value={exp.bio || ""} onChange={(e) => update(i, { bio: e.target.value })} data-testid={`expert-bio-${exp.id}`} /></div>
                <div><label className="text-[11.5px] text-[#6b7280]">Areas of expertise (comma-separated)</label>
                  <Input value={Array.isArray(exp.expertise) ? exp.expertise.join(", ") : (exp.expertise || "")}
                    onChange={(e) => update(i, { expertise: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })}
                    data-testid={`expert-expertise-${exp.id}`} />
                </div>
              </div>
            </div>
          </div>
        ))}
        {experts.length === 0 && <div className="text-center text-sm text-[#6b7280] py-10">No experts yet. Click "Add expert" to create one.</div>}
      </div>
    </div>
  );
}
